import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  MenuItem
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
  GridToolbar,
  GridPaginationModel
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { Tenant, CreateTenantData, UpdateTenantData } from '../types';
import apiService from '../services/api';

const TenantManagement: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [formData, setFormData] = useState<CreateTenantData>({
    tenantId: '',
    domain: '',
    bypassAuth: false,
    defaultLanguage: 'he'
  });
  const [formErrors, setFormErrors] = useState<Partial<CreateTenantData>>({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getTenants();
      // Normalize rows to ensure all fields exist and avoid runtime errors
      const normalized = (Array.isArray(data) ? data : []).map((t, idx) => ({
        _id: t?._id ?? `${t?.tenantId ?? 'unknown'}-${t?.domain ?? 'unknown'}-${idx}`,
        tenantId: t?.tenantId ?? '',
        domain: t?.domain ?? '',
        bypassAuth: typeof t?.bypassAuth === 'boolean' ? t.bypassAuth : undefined,
        defaultLanguage: (t as any)?.defaultLanguage || 'he',
        createdAt: t?.createdAt ?? '',
        updatedAt: t?.updatedAt ?? ''
      }));
      setTenants(normalized as unknown as Tenant[]);
    } catch (error: any) {
      setError(error.response?.data?.message || error.message || 'Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (tenant?: Tenant) => {
    if (tenant) {
      setEditingTenant(tenant);
      setFormData({
        tenantId: tenant.tenantId,
        domain: tenant.domain,
        bypassAuth: tenant.bypassAuth,
        defaultLanguage: (tenant as any)?.defaultLanguage || 'he'
      });
    } else {
      setEditingTenant(null);
      setFormData({
        tenantId: '',
        domain: '',
        bypassAuth: false,
        defaultLanguage: 'he'
      });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTenant(null);
    setFormData({
      tenantId: '',
      domain: '',
      bypassAuth: false,
      defaultLanguage: 'he'
    });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Partial<CreateTenantData> = {};
    
    if (!formData.bypassAuth) {
      if (!formData.tenantId.trim()) {
        errors.tenantId = 'Tenant ID is required unless bypass is enabled';
      }
    }
    
    if (!formData.domain.trim()) {
      errors.domain = 'Domain is required';
    } else if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.domain)) {
      errors.domain = 'Please enter a valid domain';
    }
    
    if (!['en', 'he'].includes((formData as any).defaultLanguage)) {
      (errors as any).defaultLanguage = 'Language must be "en" or "he"';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      if (editingTenant) {
        const updateData: UpdateTenantData = {
          tenantId: formData.tenantId,
          domain: formData.domain,
          bypassAuth: formData.bypassAuth,
          defaultLanguage: (formData as any).defaultLanguage
        };
        await apiService.updateTenant(editingTenant._id, updateData);
        setSuccessMessage('Tenant updated successfully!');
      } else {
        await apiService.createTenant(formData);
        setSuccessMessage('Tenant created successfully!');
      }
      
      await fetchTenants();
      handleCloseDialog();
    } catch (error: any) {
      setError(error.response?.data?.message || error.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (tenant: Tenant) => {
    setTenantToDelete(tenant);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tenantToDelete) return;
    
    try {
      setError(null);
      await apiService.deleteTenant(tenantToDelete._id);
      setSuccessMessage('Tenant deleted successfully!');
      await fetchTenants();
      setDeleteDialogOpen(false);
      setTenantToDelete(null);
    } catch (error: any) {
      setError(error.response?.data?.message || error.message || 'Failed to delete tenant');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTenantToDelete(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'tenantId',
      headerName: 'Tenant ID',
      width: 200,
      flex: 1,
      renderCell: (params: any) => params?.row?.tenantId ?? ''
    },
    {
      field: 'domain',
      headerName: 'Domain',
      width: 250,
      flex: 1,
      renderCell: (params: any) => params?.row?.domain ?? ''
    },
    {
      field: 'bypassAuth',
      headerName: 'Bypass Auth',
      width: 150,
      renderCell: (params: any) => {
        const v = params?.row?.bypassAuth;
        return v === undefined || v === null ? '' : (v ? 'Yes' : 'No');
      }
    },
    {
      field: 'defaultLanguage',
      headerName: 'Default Language',
      width: 160,
      renderCell: (params: any) => {
        const v = params?.row?.defaultLanguage;
        return v === 'en' ? 'English' : 'Hebrew';
      }
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 180,
      renderCell: (params: any) => {
        const v = params?.row?.createdAt;
        return v ? new Date(v).toLocaleString() : '';
      }
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 180,
      renderCell: (params: any) => {
        const v = params?.row?.updatedAt;
        return v ? new Date(v).toLocaleString() : '';
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteClick(params.row)}
        />
      ]
    }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            Tenant Management
          </Typography>
          <Box>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={fetchTenants} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ ml: 1 }}
            >
              Add Tenant
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        <DataGrid
          autoHeight
          rows={(tenants || []).map((r) => ({
            _id: r?._id ?? `${r?.tenantId ?? 'row'}-${r?.domain ?? 'no-domain'}`,
            tenantId: r?.tenantId ?? '',
            domain: r?.domain ?? '',
            bypassAuth: (r as any)?.bypassAuth,
            defaultLanguage: (r as any)?.defaultLanguage || 'he',
            createdAt: (r as any)?.createdAt ?? '',
            updatedAt: (r as any)?.updatedAt ?? ''
          }))}
          columns={columns}
          loading={loading}
          getRowId={(row) => row?._id}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50]}
          disableRowSelectionOnClick
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTenant ? 'Edit Tenant' : 'Add New Tenant'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Tenant ID"
              fullWidth
              variant="outlined"
              value={formData.tenantId}
              onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
              error={!!formErrors.tenantId}
              helperText={formErrors.tenantId}
              disabled={submitting || formData.bypassAuth}
            />
            <TextField
              margin="dense"
              label="Domain"
              fullWidth
              variant="outlined"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              error={!!formErrors.domain}
              helperText={formErrors.domain}
              disabled={submitting}
            />
            <TextField
              select
              margin="dense"
              label="Default Language"
              fullWidth
              variant="outlined"
              value={(formData as any).defaultLanguage}
              onChange={(e) => setFormData({ ...formData, defaultLanguage: e.target.value as 'en' | 'he' })}
              disabled={submitting}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="he">Hebrew</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.bypassAuth}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData({ ...formData, bypassAuth: checked });
                    if (checked) {
                      setFormErrors((prev) => ({ ...prev, tenantId: undefined }));
                    }
                  }}
                  disabled={submitting}
                />
              }
              label="Bypass Authentication"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting ? 'Saving...' : editingTenant ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the tenant "{tenantToDelete?.tenantId}" with domain "{tenantToDelete?.domain}"?
          </Typography>
          <Typography color="error" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantManagement;
